import { api } from "./apiSlice";
import * as FileSaver from "file-saver";
import { showMessage } from "../../helpers";
import { adminPartnerApi } from "./adminPartnerApi";
import { adminProductsApi } from "./adminProductsApi";

export const royaltySalesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getPartnersProductList: builder.query({
      query: () => "/partner-active-product-list/",
      providesTags: ["RoyaltySales"],
    }),

    getRoyaltyPartnerList: builder.query({
      query: () => "/royalty-partner-list/",
      providesTags: ["RoyaltySales"],
    }),

    getSalesRequestData: builder.query({
      query: (params = {}) => {
        const {
          isApproved,
          partner_id,
          page,
          page_size,
          limit,
          offset,
          partner_name = [],
          product = [],
          country_of_sale = [],
          status = [],
          request_id = [],
          request_id_type = [],
          ordering,
          ...otherParams
        } = params;

        let url = "/partner/royalty-sales-request/";
        const queryParams = new URLSearchParams();

        // Pagination - prefer limit/offset
        if (limit !== undefined) {
          queryParams.append('limit', limit);
        } else if (page_size !== undefined) {
          queryParams.append('limit', page_size);
        }

        if (offset !== undefined) {
          queryParams.append('offset', offset);
        } else if (page !== undefined && page_size !== undefined) {
          const calculatedOffset = (page - 1) * page_size;
          queryParams.append('offset', calculatedOffset);
        }

        // Other standard params
        if (isApproved !== undefined) {
          queryParams.append('is_approved', isApproved ? "true" : "false");
        }

        if (partner_id) {
          queryParams.append('partner_id', partner_id);
        }

        // Sorting
        if (ordering) {
          queryParams.append('ordering', ordering);
        }

        // Array filters
        partner_name.forEach(name => queryParams.append('partner_name', name));
        product.forEach(prod => queryParams.append('product', prod));
        country_of_sale.forEach(country => queryParams.append('country_of_sale', country));
        status.forEach(stat => queryParams.append('status', stat));
        request_id.forEach(id => queryParams.append('request_id', id));

        // ✅ New: Append request_id_type array values individually
        request_id_type.forEach(type => queryParams.append('request_id_type', type));

        // Remaining params
        Object.entries(otherParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            // If array, append each value separately
            if (Array.isArray(value)) {
              value.forEach(v => queryParams.append(key, v));
            } else {
              queryParams.append(key, value);
            }
          }
        });

        // Build final URL
        const queryString = queryParams.toString();
        if (queryString) {
          url += `?${queryString}`;
        }

        return url;
      },
      providesTags: ["RoyaltySales"],
    }),

    getSalesMappedData: builder.query({
      query: (request_id) => {
        if (!request_id) {
          return { url: "/partner/royalty-sales-mapped-data/" };
        }
        return {
          url: "/partner/royalty-sales-mapped-data/",
          params: { request_id },
        };
      },
      providesTags: ["RoyaltySales"],
    }),

    getSingleSalesRequestData: builder.query({
      query: (id) => `/partner/royalty-sales-request/${id}/`,
      providesTags: (result, error, id) => [{ type: "RoyaltySales", id }],
    }),

    deleteSalesRequest: builder.mutation({
      query: (id) => ({
        url: `/partner/royalty-sales-request/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["RoyaltySales"],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          showMessage("success", "Removed!");
        } catch (error) {
          console.log(error);
        }
      },
    }),

    addSalesRequest: builder.mutation({
      query: (data) => ({
        url: "/partner/royalty-sales-request/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["RoyaltySales"],
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          showMessage("success", "Sales Request raised succcessfully!");
        } catch (error) {
          let errorMsg = "";
          if (error && error.error) {
            // Handle both array and non-array error formats
            if (Array.isArray(error.error)) {
              errorMsg = error.error.filter((value) => value !== "None");
            } else if (typeof error.error === 'string') {
              errorMsg = [error.error];
            }
          }
          if (errorMsg && errorMsg.length) {
            showMessage("error", "Error");
          }
        }
      },
    }),

    editSalesRequest: builder.mutation({
      query: ({ id, data, status }) => ({
        url: `/partner/royalty-sales-request/${id}/`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "RoyaltySales", id },
        "RoyaltySales",
      ],
      async onQueryStarted({ id, data, status }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          if (status) {
            showMessage("success", status);
          } else {
            showMessage("success", "Request edited succcessfully!");
          }
        } catch (error) {
          let errorMsg = "";
          try {
            if (error && error.error) {
              // Handle both array and non-array error formats
              if (Array.isArray(error.error)) {
                errorMsg = error.error.filter((value) => value !== "None");
              } else if (typeof error.error === 'string') {
                errorMsg = [error.error];
              } else if (typeof error.error === 'object') {
                // Handle object error format
                errorMsg = [JSON.stringify(error.error)];
              }
            }
          } catch (e) {
            console.log("Error parsing error:", e);
          }
          if (errorMsg && errorMsg.length) {
            showMessage("error", errorMsg[0]);
            showMessage("error", "Error");
          }
        }
      },
    }),

    downloadPOApprovalRequestsDocument: builder.query({
      query: (requestObj) => ({
        url: "/po_approval/royalty/download/",
        method: "POST",
        body: requestObj,
        responseHandler: async (response) => {
          // Return blob for file download
          return await response.blob();
        },
      }),
      // Don't cache this as it's a download
      // We'll handle saving the file in component using this hook
    }),

    getLimitedViewSalesRequestData: builder.query({
      query: (params = {}) => {
        let url = "/partner/royalty-sales-request/limited-view/";
        const queryParams = new URLSearchParams();

        // Only mapping_id is supported as a search param
        if (params.mapping_id) {
          queryParams.append("search", params.mapping_id);
        }

        // Add partner_id for admin users
        if (params.partner_id) {
          queryParams.append("partner_id", params.partner_id);
        }

        const queryString = queryParams.toString();
        if (queryString) {
          url += `?${queryString}`;
        }
        return url;
      },
      providesTags: ["RoyaltySales"],
    }),
  }),
});

export const {
  useGetPartnersProductListQuery,
  useGetRoyaltyPartnerListQuery,
  useGetSalesRequestDataQuery,
  useGetSalesMappedDataQuery,
  useGetSingleSalesRequestDataQuery,
  useDeleteSalesRequestMutation,
  useAddSalesRequestMutation,
  useEditSalesRequestMutation,
  useLazyDownloadPOApprovalRequestsDocumentQuery,
  useGetLimitedViewSalesRequestDataQuery
} = royaltySalesApi;

// Export queries for filter options from other APIs
export const {
  useGetPartnerListQuery,
} = adminPartnerApi;

export const {
  useGetProductListQuery,
} = adminProductsApi;

// Country list is already available in adminPartnerApi
export const {
  useGetCountryListQuery,
  useGetCountryListDiagnosticQuery,
} = adminPartnerApi;
